import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {useNavigate } from 'react-router-dom';

import { useLoginMutation } from '@/store/api/authApi';
import { useGetContactSupportQuery } from '@/store/api/userApi';
import { useAppDispatch } from '@/hooks/redux';
import { login } from '@/store/slices/authSlice';
import { setChangePasswordModalOpen } from '@/store/slices/uiSlice';
import { Icons } from '@/utils/Icons';
import { getDeviceType, isGuestUser, showToast } from '@/utils';
import { loginSchema } from '@/utils/validation';
import OTPModal from '@/components/OTPModal';
import ForgotPasswordModal from '@/components/ForgotPasswordModal';
import { useLockBodyScroll } from '@/hooks';
import { useOutsideClick } from '@/hooks/useOutsideClick';

type LoginFormData = z.infer<typeof loginSchema>;

interface Props {
  open: boolean;
  onClose: () => void;
}

const LoginModal: React.FC<Props> = ({ open, onClose }) => {
  useLockBodyScroll(open);
  const modalRef = useOutsideClick<HTMLDivElement>(() => {
    if (open) onClose();
  }, open);

  const [loginMutation, { isLoading }] = useLoginMutation();
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [verification, setVerification] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState(false);
  const [verificationData, setverificationData] = useState<{ username: string; type: string }>({
    username: '',
    type: '',
  });

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isGuest = isGuestUser();
  useGetContactSupportQuery(undefined, { skip: isGuest });

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: { username: '', password: '' },
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      const device = getDeviceType();
      const result = await loginMutation({
        username: data.username.trim(),
        password: data.password.trim(),
        device: device,
      }).unwrap();

      if (result?.accessToken) {
        if (result?.role?.name?.toLowerCase() !== 'user') {
          showToast('User does not exist', 'error');
          return;
        }
        dispatch(
          login({
            type: result.type,
            accessToken: result.accessToken,
          })
        );
      }

      if (result?.isTelegramEnabled || result?.mfaRequired) {
        setverificationData({
          username: data.username,
          type: result?.isTelegramEnabled ? 'telegram' : 'number',
        });
        setVerification(true);
      } else if (result?.passwordChanged) {
        onClose();
        navigate('/dashboard');
      } else {
        onClose();
        dispatch(setChangePasswordModalOpen(true));
      }
    } catch (e: any) {
      showToast(e.data?.message ?? 'Login failed', 'error');
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-[4vh]">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      <div
        ref={modalRef}
        className="relative z-10 w-full max-w-[540px] rounded-md overflow-hidden shadow-xl transform transition-all duration-300 ease-out"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-primary p-6 text-center relative">
          <button
            onClick={onClose}
            className="absolute right-4 top-4 text-white"
            aria-label="Close"
          >
            <Icons.Cross />
          </button>

          <img src="/images/logo/logo.png" alt="logo" className="mx-auto h-20 w-auto" />
        </div>

        {/* Body */}
        <div className="bg-primary p-6">
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
            <label className="text-white font-semibold">Username</label>
            <input
              {...register('username')}
              placeholder="Username"
              className="w-full px-4 py-2 rounded-md bg-white text-black focus:outline-none"
            />
            {errors.username && <p className="text-danger text-xs">{errors.username.message}</p>}

            <label className="text-white font-semibold">Password</label>
            <div className="relative">
              <input
                {...register('password')}
                placeholder="Password"
                type={showPassword ? 'text' : 'password'}
                className="w-full px-4 py-2 rounded-md bg-white text-black focus:outline-none"
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-2 top-2 bg-black/70 rounded px-2 py-1 text-white"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <Icons.openEye /> : <Icons.closeEye />}
              </button>
            </div>
            {errors.password && <p className="text-danger text-xs">{errors.password.message}</p>}

            {/* <label className="flex items-center gap-2 text-white">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4 bg-primary"
              />{' '}
              Keep me signed in
            </label> */}

            <div className="w-full">
              <button
                type="submit"
                disabled={isLoading}
                className="w-full rounded-full border-2 border-white bg-white py-2 font-semibold text-teal-600 shadow-[inset_0_0_0_2px_#16a6b5] disabled:cursor-not-allowed disabled:opacity-60"
              >
                Sign In
              </button>
            </div>
          </form>

          {/* Decorative rounded bar as in reference */}
          {/* <div className="mt-4">
            <div className="w-full h-10 rounded-full bg-white/60 border border-white/30" />
          </div> */}

          <div className="mt-4">
            <button className="w-full flex items-center justify-center gap-2 text-white">
              <Icons.Android className="text-green-400 text-4xl" /> Download Apk
            </button>
          </div>
        </div>

        <ForgotPasswordModal
          open={forgotPasswordOpen}
          onClose={() => setForgotPasswordOpen(false)}
        />
        <OTPModal
          data={verificationData}
          open={verification}
          onClose={() => setVerification(false)}
        />
      </div>
    </div>
  );
};

export default LoginModal;
